import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { Modal } from '../../components';
import css from './AddItemModal.css';

function generateId() {
  return Math.random().toString(36).slice(2);
}

export function AddItemModal({ open, onOpenChange, preselectedProductId, products, sdk }) {
  const intl = useIntl();
  const { providerId } = useParams();
  const queryClient = useQueryClient();

  const [selectedProductId, setSelectedProductId] = useState('');
  const [serials, setSerials] = useState([{ id: generateId(), value: '' }]);
  const [apiError, setApiError] = useState(null);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!open) {
      setSelectedProductId('');
      setSerials([{ id: generateId(), value: '' }]);
      setApiError(null);
    }
  }, [open]);

  const isProductLocked = !!preselectedProductId;
  const activeProductId = preselectedProductId || selectedProductId;

  const selectedProductData = products.find(
    p => p.relationships.catalogItem?.data?.id.uuid === activeProductId
  );

  const { mutate: handleCreatePieces, isLoading: isSubmitting } = useMutation({
    mutationFn: (codes) => {
      setApiError(null);
      
      const pieces = codes.map(code => ({
        attributes: {
          catalogItemId: activeProductId,
          code: code.trim(),
          providerId: providerId,
        },
      }));
      
      return sdk.newSdk.pieces.createMany(pieces, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ownListings']);
      queryClient.invalidateQueries(['pieces']);
      onOpenChange(false);
    },
    onError: error => {
      const rawErrorResponse = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawErrorResponse === 'string') {
        errorMessage = rawErrorResponse.trim();
      } else if (rawErrorResponse?.errors?.[0]?.detail) {
        errorMessage = rawErrorResponse.errors[0].detail;
      } else if (rawErrorResponse?.message) {
        errorMessage = rawErrorResponse.message;
      }

      if (!errorMessage) {
        setApiError(
          intl.formatMessage({
            id: 'AddItemModal.defaultApiError',
            defaultMessage: 'Nie można dodać sztuk. Jeden z podanych numerów seryjnych prawdopodobnie już istnieje w systemie.',
          })
        );
      } else {
        setApiError(errorMessage);
      }
    },
  });

  const addRow = () => {
    if (isSubmitting) return;
    const newId = generateId();
    setSerials(prev => [...prev, { id: newId, value: '' }]);

    setTimeout(() => {
      const lastIndex = serials.length;
      if (inputRefs.current[lastIndex]) {
        inputRefs.current[lastIndex].focus();
      }
    }, 50);
  };

  const removeRow = id => {
    if (isSubmitting) return;
    setSerials(prev => prev.filter(s => s.id !== id));
    setApiError(null);
  };

  const updateRow = (id, value) => {
    setSerials(prev => prev.map(s => (s.id === id ? { ...s, value } : s)));
    if (apiError) setApiError(null);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      const currentValue = serials[index]?.value.trim();

      if (currentValue) {
        e.preventDefault();
        const isLastRow = index === serials.length - 1;
        if (isLastRow) {
          addRow();
        } else if (inputRefs.current[index + 1]) {
          inputRefs.current[index + 1].focus();
        }
      } 
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    const filledCodes = serials
      .map(s => s.value.trim())
      .filter(val => val.length > 0);

    if (!activeProductId || filledCodes.length === 0 || !providerId) return;

    handleCreatePieces(filledCodes);
  };

  const filledCount = serials.filter(s => s.value.trim()).length;
  const canSubmit = !!activeProductId && filledCount > 0 && !isSubmitting;

  const getSubmitButtonLabel = () => {
    if (isSubmitting) {
      return intl.formatMessage({ id: 'AddItemModal.submittingButton', defaultMessage: 'Zapisywanie...' });
    }
    if (filledCount > 1) {
      return intl.formatMessage(
        {
          id: 'AddItemModal.submitMultipleButton',
          defaultMessage: '{count, plural, few {Dodaj {count} sztuki} other {Dodaj {count} sztuk}}',
        },
        { count: filledCount }
      );
    }
    if (isProductLocked) {
      return intl.formatMessage({ id: 'AddItemModal.submitSingleLockedButton', defaultMessage: 'Dodaj sztukę' });
    }
    return intl.formatMessage({ id: 'AddItemModal.submitSingleUnlockedButton', defaultMessage: 'Dodaj produkt' });
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      id="AddItemModal"
      title={
        isProductLocked
          ? intl.formatMessage({ id: 'AddItemModal.titleLocked', defaultMessage: 'Dodaj nowe sztuki' })
          : intl.formatMessage({ id: 'AddItemModal.titleUnlocked', defaultMessage: 'Dodaj produkt' })
      }
      onManageDisableScrolling={() => {}}
      usePortal
    >
      <form onSubmit={handleSubmit} className={css.modalForm}>
        <div className={css.inputWrapper}>
          <label htmlFor="modalProductSelect" className={css.label}>
            {intl.formatMessage({ id: 'AddItemModal.productModelLabel', defaultMessage: 'Model produktu' })}
          </label>
          <select
            id="modalProductSelect"
            value={activeProductId}
            onChange={e => setSelectedProductId(e.target.value)}
            required
            disabled={isProductLocked || isSubmitting}
            className={`${css.selectInput} ${isProductLocked ? css.inputDisabled : ''}`}
          >
            <option value="">
              {intl.formatMessage({ id: 'AddItemModal.selectPlaceholder', defaultMessage: 'Wybierz model z katalogu...' })}
            </option>
            {products.filter(x => !!x.relationships.catalogItem?.data?.id.uuid).map(product => (
              <option key={product.relationships.catalogItem?.data?.id.uuid} value={product.relationships.catalogItem?.data?.id.uuid}>
                {product.attributes.title}
              </option>
            ))}
          </select>
          {isProductLocked && (
            <p className={css.helperText}>
              {intl.formatMessage({ id: 'AddItemModal.helperText', defaultMessage: 'Dodajesz sztuki do produktu:' })}{' '}
              <strong>{selectedProductData?.attributes.title}</strong>
            </p>
          )}
        </div>

        <div className={css.serialsSection}>
          <div className={css.serialsHeader}>
            <label className={css.label}>
              {intl.formatMessage({ id: 'AddItemModal.serialsHeaderLabel', defaultMessage: 'Numery ID sztuk' })}
            </label>
            {filledCount > 0 && (
              <span className={css.badge}>
                <FormattedMessage
                  id="AddItemModal.badgeCount"
                  defaultMessage="{count} {count, plural, one {sztuka} few {sztuki} many {sztuk} other {sztuki}}"
                  values={{ count: filledCount }}
                />
              </span>
            )}
          </div>

          <div className={css.serialsList}>
            {serials.map((serial, index) => (
              <div key={serial.id} className={css.serialRow}>
                <span className={css.rowNumber}>{index + 1}.</span>
                <input
                  ref={el => (inputRefs.current[index] = el)}
                  type="text"
                  value={serial.value}
                  disabled={isSubmitting}
                  onChange={e => updateRow(serial.id, e.target.value)}
                  onKeyDown={e => handleKeyDown(e, index)}
                  placeholder={intl.formatMessage({
                    id: 'AddItemModal.serialPlaceholder',
                    defaultMessage: 'Zeskanuj naklejkę z numerem ID sztuki',
                  })}
                  className={css.textInput}
                />
                {serials.length > 1 && (
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => removeRow(serial.id)}
                    className={css.removeRowBtn}
                    title={intl.formatMessage({ id: 'AddItemModal.removeButtonTitle', defaultMessage: 'Usuń' })}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={addRow}
            className={css.addRowBtn}
          >
            {intl.formatMessage({ id: 'AddItemModal.addRowButton', defaultMessage: '+ Dodaj kolejną sztukę' })}
          </button>
        </div>

        {apiError && (
          <div className={css.errorBanner}>
            <span className={css.errorIcon}>⚠️</span>
            <div className={css.errorMessage}>{apiError}</div>
          </div>
        )}

        <div className={css.modalActions}>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
            className={css.cancelBtn}
          >
            {intl.formatMessage({ id: 'AddItemModal.cancelButton', defaultMessage: 'Anuluj' })}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className={css.submitBtn}
          >
            {getSubmitButtonLabel()}
          </button>
        </div>
      </form>
    </Modal>
  );
}